import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  Tooltip,
  useColorModeValue,
  useToast,
  Skeleton,
  HStack,
  VStack,
  Collapse,
  Spinner,
} from '@chakra-ui/react';
import {
  MdNotifications,
  MdNotificationsActive,
  MdCheckCircle,
  MdOutlineFileUpload,
  MdOutlineEdit,
  MdOutlineDone,
  MdOutlineClose,
  MdOutlineRefresh,
  MdOutlineAccessTime,
  MdExpandMore,
  MdExpandLess,
  MdPeople,
} from 'react-icons/md';
import { FiAlertCircle } from 'react-icons/fi';
import { TbFileText } from 'react-icons/tb';
import axios from 'axios';
import moment from 'moment';
import { motion, AnimatePresence } from 'framer-motion';
import baseUrl from 'Base_Url/baseUrl';
import socketService from '../../services/socket';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();

  const menuRef = useRef();
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const socketEventCooldownRef = useRef({});
  const pendingNotificationsRef = useRef([]);
  const mountedRef = useRef(true);

  const FETCH_COOLDOWN_MS = 5000;
  const UNREAD_COUNT_COOLDOWN_MS = 10000;
  const lastUnreadCountFetchRef = useRef(0);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const badgeBg = useColorModeValue('red.500', 'red.400');
  const iconColor = useColorModeValue('gray.600', 'gray.300');
  const accordionBg = useColorModeValue('gray.50', 'gray.900');
  const unreadBg = useColorModeValue('blue.50', 'blue.900');
  const readBg = useColorModeValue('white', 'gray.800');

  const toast = useToast();

  const notificationTypes = {
    file_upload: {
      icon: MdOutlineFileUpload,
      color: 'blue.500',
      bg: 'blue.100',
      label: 'File Uploaded',
    },
    file_edit: {
      icon: MdOutlineEdit,
      color: 'orange.500',
      bg: 'orange.100',
      label: 'File Edited',
    },
    file_approved: {
      icon: MdOutlineDone,
      color: 'green.500',
      bg: 'green.100',
      label: 'File Approved',
    },
    file_rejected: {
      icon: MdOutlineClose,
      color: 'red.500',
      bg: 'red.100',
      label: 'File Rejected',
    },
    file_processed: {
      icon: TbFileText,
      color: 'purple.500',
      bg: 'purple.100',
      label: 'File Processed',
    },
    system_alert: {
      icon: FiAlertCircle,
      color: 'yellow.500',
      bg: 'yellow.100',
      label: 'System Alert',
    },
  };

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUserId(user._id);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  }, []);

  const NotificationSkeleton = () => (
    <VStack align="stretch" spacing={3} p={3}>
      {[1, 2, 3].map((i) => (
        <Flex key={i} p={3} borderRadius="md" bg="gray.50">
          <Skeleton circle size="8" mr={3} />
          <Box flex="1">
            <Skeleton height="3" width="60%" mb={2} />
            <Skeleton height="2" width="90%" mb={1} />
            <Skeleton height="2" width="40%" />
          </Box>
        </Flex>
      ))}
    </VStack>
  );

  const formatTime = useCallback((date) => {
    if (!date) return '-';
    try {
      const now = moment();
      const notificationTime = moment(date);

      if (now.diff(notificationTime, 'minutes') < 1) {
        return 'Just now';
      } else if (now.diff(notificationTime, 'hours') < 1) {
        return `${now.diff(notificationTime, 'minutes')}m ago`;
      } else if (now.diff(notificationTime, 'days') < 1) {
        return `${now.diff(notificationTime, 'hours')}h ago`;
      } else if (now.diff(notificationTime, 'days') < 7) {
        return `${now.diff(notificationTime, 'days')}d ago`;
      } else {
        return notificationTime.format('DD MMM YYYY');
      }
    } catch {
      return '-';
    }
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      if (!notificationSoundRef.current) {
        notificationSoundRef.current = new (
          window.AudioContext || window.webkitAudioContext
        )();
      }

      const oscillator = notificationSoundRef.current.createOscillator();
      const gainNode = notificationSoundRef.current.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(notificationSoundRef.current.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(
        0.3,
        notificationSoundRef.current.currentTime,
      );
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        notificationSoundRef.current.currentTime + 0.5,
      );

      oscillator.start(notificationSoundRef.current.currentTime);
      oscillator.stop(notificationSoundRef.current.currentTime + 0.5);
    } catch (error) {
      // Silent fail for audio
    }
  }, []);

  const isNotificationReadByCurrentUser = useCallback(
    (notification) => {
      if (!currentUserId || !notification) return true;

      if (notification.isRead !== undefined) {
        return notification.isRead;
      }

      if (
        notification.metadata?.readBy &&
        Array.isArray(notification.metadata.readBy)
      ) {
        return notification.metadata.readBy.some(
          (item) => item.user?.toString() === currentUserId?.toString(),
        );
      }

      return notification.read || false;
    },
    [currentUserId],
  );

  const fetchNotifications = useCallback(
    async (pageNum = 1, refresh = false, force = false) => {
      if (!mountedRef.current) return;

      if (isFetchingRef.current && !force) {
        return;
      }

      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTimeRef.current;

      if (timeSinceLastFetch < FETCH_COOLDOWN_MS && !force) {
        return;
      }

      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(
        async () => {
          try {
            const token = localStorage.getItem('token');
            if (!token) {
              return;
            }

            isFetchingRef.current = true;
            lastFetchTimeRef.current = Date.now();

            if (pageNum === 1) setLoading(true);
            if (pageNum > 1) setLoadingMore(true);

            const response = await axios.get(`${baseUrl}/notifications`, {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                page: pageNum,
                limit: 10,
                sortBy: 'createdAt',
                sortOrder: 'desc',
              },
            });

            let notificationsData = [];
            let paginationData = {};

            if (response.data && response.data.status === true) {
              const apiData = response.data.data;

              if (apiData && apiData.data) {
                notificationsData = apiData.data;
                paginationData = apiData.pagination || {};
              } else if (Array.isArray(apiData)) {
                notificationsData = apiData;
              } else if (Array.isArray(response.data)) {
                notificationsData = response.data;
              }
            }

            if (!mountedRef.current) return;

            if (!Array.isArray(notificationsData)) {
              notificationsData = [];
            }

            notificationsData = notificationsData.map((notification) => ({
              ...notification,
              isRead: isNotificationReadByCurrentUser(notification),
            }));

            if (refresh || pageNum === 1) {
              setNotifications(notificationsData);
            } else {
              setNotifications((prev) => {
                const newNotifications = notificationsData.filter(
                  (newNotif) =>
                    !prev.some((prevNotif) => prevNotif._id === newNotif._id),
                );
                return [...prev, ...newNotifications];
              });
            }

            setHasMore(paginationData.hasNext || false);
            setPage(pageNum);
          } catch (error) {
            // Silent fail
          } finally {
            if (mountedRef.current) {
              setLoading(false);
              setLoadingMore(false);
            }
            isFetchingRef.current = false;
          }
        },
        force ? 0 : 300,
      );
    },
    [isNotificationReadByCurrentUser],
  );

  const fetchUnreadCount = useCallback(async (force = false) => {
    if (!mountedRef.current) return;

    const now = Date.now();
    const timeSinceLastFetch = now - lastUnreadCountFetchRef.current;

    if (timeSinceLastFetch < UNREAD_COUNT_COOLDOWN_MS && !force) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      lastUnreadCountFetchRef.current = Date.now();

      const response = await axios.get(
        `${baseUrl}/notifications/unread-count`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      let count = 0;

      if (response.data && response.data.status === true) {
        const apiData = response.data.data;

        // Handle different response structures
        if (apiData && typeof apiData === 'number') {
          count = apiData;
        } else if (apiData && apiData.count !== undefined) {
          count = apiData.count;
        } else if (apiData && apiData.data !== undefined) {
          count = typeof apiData.data === 'number' ? apiData.data : 0;
        }
      }

      if (mountedRef.current) {
        setUnreadCount(count);
      }
    } catch (error) {
      // Silent fail
    }
  }, []);

  const handleSocketNotification = useCallback(
    (notificationData) => {
      if (!mountedRef.current || !currentUserId) return;

      let notification = notificationData;
      let isWrapped = false;

      if (notificationData.notification) {
        notification = notificationData.notification;
        isWrapped = true;
      }

      if (!notification || !notification._id) {
        return;
      }

      const isForCurrentUser = notification.createdFor?.includes(currentUserId);
      if (!isForCurrentUser && !isWrapped) {
        return;
      }

      const isDuplicate = pendingNotificationsRef.current.some(
        (n) => n._id === notification._id,
      );

      if (isDuplicate) {
        return;
      }

      pendingNotificationsRef.current = [
        { ...notification, receivedAt: new Date() },
        ...pendingNotificationsRef.current.slice(0, 9),
      ];

      const isRead = isNotificationReadByCurrentUser(notification);

      const notificationType = notification.type || 'system_alert';
      const typeConfig =
        notificationTypes[notificationType] || notificationTypes.system_alert;

      if (!isRead) {
        toast({
          title:
            notification.title ||
            notificationType.replace('_', ' ').toUpperCase(),
          description: notification.message
            ? notification.message.length > 100
              ? `${notification.message.substring(0, 100)}...`
              : notification.message
            : 'New notification',
          status:
            notificationType === 'file_rejected'
              ? 'error'
              : notificationType === 'file_approved'
                ? 'success'
                : notificationType === 'file_upload'
                  ? 'info'
                  : notificationType === 'file_edit'
                    ? 'warning'
                    : 'info',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
          icon: <Icon as={typeConfig.icon} color={typeConfig.color} />,
        });

        playNotificationSound();
      }

      setNotifications((prev) => {
        const exists = prev.find((n) => n._id === notification._id);
        if (!exists) {
          const newNotification = {
            ...notification,
            isRead: isRead,
            createdAt: notification.createdAt || new Date().toISOString(),
          };
          return [newNotification, ...prev];
        }
        return prev.map((n) =>
          n._id === notification._id
            ? { ...n, ...notification, isRead: isRead }
            : n,
        );
      });

      if (!isRead) {
        setUnreadCount((prev) => prev + 1);
      }
    },
    [
      currentUserId,
      isNotificationReadByCurrentUser,
      notificationTypes,
      playNotificationSound,
      toast,
    ],
  );

  const handleSocketSync = useCallback(
    (syncData) => {
      if (!mountedRef.current) return;

      const now = Date.now();
      const lastEventTime = socketEventCooldownRef.current[syncData.type] || 0;

      if (now - lastEventTime < 2000) {
        return;
      }

      socketEventCooldownRef.current[syncData.type] = now;

      switch (syncData.type) {
        case 'connected':
          setSocketConnected(true);
          setTimeout(() => {
            if (!isFetchingRef.current) {
              fetchNotifications(1, true);
              fetchUnreadCount(true);
            }
          }, 1000);
          break;

        case 'disconnected':
        case 'connect_error':
          setSocketConnected(false);
          break;

        case 'unread_count':
        case 'unread_count_update':
          setUnreadCount(syncData.count || 0);
          break;

        case 'notification_update':
          if (syncData.notificationId) {
            setNotifications((prev) =>
              prev.map((notif) =>
                notif._id === syncData.notificationId
                  ? {
                    ...notif,
                    isRead: true,
                    readAt: syncData.readAt || new Date().toISOString(),
                    metadata: {
                      ...notif.metadata,
                      readBy: [
                        ...(notif.metadata?.readBy || []),
                        {
                          user: currentUserId,
                          readAt: syncData.readAt || new Date().toISOString(),
                        },
                      ],
                    },
                  }
                  : notif,
              ),
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
          break;

        case 'all_notifications_read':
          setNotifications((prev) =>
            prev.map((notif) => {
              const alreadyRead = notif.metadata?.readBy?.some(
                (item) => item.user?.toString() === currentUserId?.toString(),
              );

              if (alreadyRead) return notif;

              return {
                ...notif,
                isRead: true,
                readAt: new Date().toISOString(),
                metadata: {
                  ...notif.metadata,
                  readBy: [
                    ...(notif.metadata?.readBy || []),
                    {
                      user: currentUserId,
                      readAt: new Date().toISOString(),
                    },
                  ],
                },
              };
            }),
          );
          setUnreadCount(0);
          break;

        case 'new_notification':
          if (syncData.notification && !isFetchingRef.current) {
            handleSocketNotification(syncData);
          }
          break;

        default:
          break;
      }
    },
    [
      currentUserId,
      fetchNotifications,
      fetchUnreadCount,
      handleSocketNotification,
    ],
  );

  const handleNotificationClick = useCallback(
    (notification) => {
      const isCurrentlyExpanded = expandedNotification === notification._id;

      if (isCurrentlyExpanded) {
        setExpandedNotification(null);
        if (!notification.isRead) {
          markAsRead(notification._id);
        }
      } else {
        setExpandedNotification(notification._id);
      }
    },
    [expandedNotification],
  );

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.patch(
          `${baseUrl}/notifications/${notificationId}/read`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.status === true) {
          // Update notification in state
          setNotifications((prev) =>
            prev.map((notif) => {
              if (notif._id === notificationId) {
                const alreadyRead = notif.metadata?.readBy?.some(
                  (item) => item.user?.toString() === currentUserId?.toString(),
                );

                if (alreadyRead) return notif;

                return {
                  ...notif,
                  isRead: true,
                  readAt: new Date().toISOString(),
                  metadata: {
                    ...notif.metadata,
                    readBy: [
                      ...(notif.metadata?.readBy || []),
                      {
                        user: currentUserId,
                        readAt: new Date().toISOString(),
                      },
                    ],
                  },
                };
              }
              return notif;
            }),
          );

          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (error) {
        // Silent fail if already read
        if (!error.response?.data?.message?.includes('already read')) {
          toast({
            title: 'Error',
            description: 'Failed to mark notification as read',
            status: 'error',
            duration: 3000,
          });
        }
      }
    },
    [currentUserId, toast],
  );

  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.patch(
        `${baseUrl}/notifications/mark-all-read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === true) {
        const data = response.data.data;
        const count = data.count || 0;

        if (count > 0) {
          setNotifications((prev) =>
            prev.map((notif) => {
              const alreadyRead = notif.metadata?.readBy?.some(
                (item) => item.user?.toString() === currentUserId?.toString(),
              );

              if (alreadyRead) return notif;

              return {
                ...notif,
                isRead: true,
                readAt: new Date().toISOString(),
                metadata: {
                  ...notif.metadata,
                  readBy: [
                    ...(notif.metadata?.readBy || []),
                    {
                      user: currentUserId,
                      readAt: new Date().toISOString(),
                    },
                  ],
                },
              };
            }),
          );

          setUnreadCount(0);
        }

        toast({
          title: 'Success',
          description:
            data.message || `${count} notification(s) marked as read`,
          status: count > 0 ? 'success' : 'info',
          duration: 2000,
        });
      }
    } catch (error) {
      if (error.response?.data?.message?.includes('already read')) {
        toast({
          title: 'Info',
          description: 'All notifications are already read',
          status: 'info',
          duration: 2000,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to mark all as read',
          status: 'error',
          duration: 3000,
        });
      }
    }
  }, [currentUserId, toast]);

  const ViewAll = () => {
    navigate('/admin/notifications');
  };

  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore && !isFetchingRef.current) {
      fetchNotifications(page + 1);
    }
  }, [hasMore, loadingMore, page, fetchNotifications]);

  const refreshNotifications = useCallback(() => {
    if (isFetchingRef.current) {
      return;
    }

    pendingNotificationsRef.current = [];
    fetchNotifications(1, true, true);
    fetchUnreadCount(true);
  }, [fetchNotifications, fetchUnreadCount]);

  const initializeSocket = useCallback(() => {
    if (!mountedRef.current) return;

    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        return;
      }

      const status = socketService.getStatus();
      if (!status.isConnected) {
        socketService.connect();
      }

      const unsubscribeNotification = socketService.onNotification(
        handleSocketNotification,
      );
      const unsubscribeSync = socketService.onSync(handleSocketSync);

      setSocketConnected(socketService.getStatus().isConnected);

      return () => {
        if (unsubscribeNotification) unsubscribeNotification();
        if (unsubscribeSync) unsubscribeSync();
      };
    } catch (error) {
      // Silent fail
    }
  }, [handleSocketNotification, handleSocketSync]);

  useEffect(() => {
    mountedRef.current = true;

    let cleanupSocket = null;
    let socketCheckInterval = null;

    const timer = setTimeout(() => {
      if (mountedRef.current && !isFetchingRef.current) {
        fetchNotifications(1, true);
        fetchUnreadCount(true);
      }
    }, 500);

    cleanupSocket = initializeSocket();

    socketCheckInterval = setInterval(() => {
      if (mountedRef.current) {
        const status = socketService.getStatus();
        setSocketConnected(status.isConnected);
      }
    }, 30000);

    return () => {
      mountedRef.current = false;

      clearTimeout(timer);
      clearInterval(socketCheckInterval);

      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      if (cleanupSocket && typeof cleanupSocket === 'function') {
        cleanupSocket();
      }

      isFetchingRef.current = false;
      pendingNotificationsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (unreadCount > 0 && notifications.length === 0) {
      if (!isFetchingRef.current) {
        fetchNotifications(1, true, true);
      }
    }
  }, [unreadCount, notifications.length]);

  return (
    <Menu closeOnSelect={false} placement="bottom-end">
      <MenuButton
        as={Button}
        variant="ghost"
        position="relative"
        p={2}
        rounded="full"
        _hover={{ bg: hoverBg }}
        _active={{ bg: hoverBg }}
      >
        <Flex align="center">
          <Icon
            as={unreadCount > 0 ? MdNotificationsActive : MdNotifications}
            w={6}
            h={6}
            color={iconColor}
          />
          {unreadCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            >
              <Badge
                position="absolute"
                top="-1"
                right="-1"
                bg={badgeBg}
                color="white"
                borderRadius="full"
                fontSize="10px"
                minW="18px"
                h="18px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 0 0 2px white"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            </motion.div>
          )}
          {!socketConnected && (
            <Badge
              position="absolute"
              bottom="-2px"
              right="-2px"
              colorScheme="red"
              fontSize="6px"
              w="8px"
              h="8px"
              borderRadius="full"
              border="2px solid white"
            />
          )}
        </Flex>
      </MenuButton>

      <MenuList
        ref={menuRef}
        w="500px"
        maxH="80vh"
        overflowY="auto"
        bg={bgColor}
        borderColor={borderColor}
        boxShadow="xl"
        borderRadius="lg"
        p={0}
        _focus={{ outline: 'none' }}
      >
        <Flex
          p={4}
          borderBottom="1px"
          borderColor={borderColor}
          justify="space-between"
          align="center"
        >
          <Flex align="center" gap={2}>
            <Text fontWeight="bold" fontSize="lg" color={textColor}>
              Notifications
            </Text>
            {unreadCount > 0 && (
              <Badge ml={2} colorScheme="red" borderRadius="full" fontSize="xs">
                {unreadCount} new
              </Badge>
            )}
            {loading && <Spinner size="sm" color="blue.500" ml={2} />}
          </Flex>
          <Flex gap={2}>
            <Tooltip label="Refresh">
              <Button
                size="sm"
                variant="ghost"
                onClick={refreshNotifications}
                isLoading={loading}
              >
                <Icon as={MdOutlineRefresh} />
              </Button>
            </Tooltip>
            {unreadCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={markAllAsRead}
              >
                Mark all read
              </Button>
            )}
          </Flex>
        </Flex>

        <Box maxH="400px" overflowY="auto">
          {loading && page === 1 ? (
            <NotificationSkeleton />
          ) : notifications.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              p={8}
              color={subTextColor}
            >
              <Icon
                as={MdCheckCircle}
                w={12}
                h={12}
                mb={4}
                opacity={0.5}
                color="green.500"
              />
              <Text fontSize="lg" fontWeight="medium" mb={2}>
                All caught up!
              </Text>
              <Text fontSize="sm" textAlign="center">
                No notifications yet
              </Text>
            </Flex>
          ) : (
            <AnimatePresence>
              {notifications.map((notification, index) => {
                if (!notification || typeof notification !== 'object') {
                  return null;
                }

                const typeConfig =
                  notificationTypes[notification.type] ||
                  notificationTypes.system_alert;
                const isExpanded = expandedNotification === notification._id;
                // CHANGED: Use isRead flag instead of notification.read
                const isUnread = !notification.isRead;

                return (
                  <motion.div
                    key={notification._id || `notif-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Accordion
                      index={isExpanded ? [0] : []}
                      allowToggle
                      onChange={() => handleNotificationClick(notification)}
                    >
                      <AccordionItem
                        border="none"
                        // CHANGED: Use isUnread for background color
                        bg={isUnread ? unreadBg : readBg}
                        p={1}
                      >
                        <AccordionButton
                          p={1}
                          _hover={{ bg: hoverBg }}
                          style={{
                            borderRadius: '10px',
                            backgroundColor: isUnread ? unreadBg : readBg,
                          }}
                        >
                          <Flex
                            flex="1"
                            align="center"
                            gap={3}
                            position="relative"
                          >
                            <Flex
                              align="center"
                              justify="center"
                              w="8"
                              h="8"
                              borderRadius="lg"
                              bg={typeConfig.bg}
                              color={typeConfig.color}
                              flexShrink={0}
                            >
                              <Icon as={typeConfig.icon} w={4} h={4} />
                            </Flex>

                            <Box flex="1" minW="0" textAlign="left">
                              <Text
                                fontSize="sm"
                                fontWeight={isUnread ? 'bold' : 'medium'}
                                color={textColor}
                                noOfLines={1}
                              >
                                {notification.title || 'Notification'}
                              </Text>

                              <Text fontSize="xs" color={subTextColor}>
                                {formatTime(notification.createdAt)}
                              </Text>
                            </Box>

                            {/* CHANGED: Show recipient count for multi-recipient notifications */}
                            {notification.totalRecipients > 1 && (
                              <Tooltip
                                label={`Sent to ${notification.totalRecipients} users`}
                              >
                                <Flex align="center" mr={2}>
                                  <Icon
                                    as={MdPeople}
                                    w={3}
                                    h={3}
                                    color="gray.500"
                                  />
                                  <Text fontSize="xs" ml={1} color="gray.500">
                                    {notification.readCount || 0}/
                                    {notification.totalRecipients}
                                  </Text>
                                </Flex>
                              </Tooltip>
                            )}
                          </Flex>

                          <AccordionIcon />
                        </AccordionButton>

                        <AccordionPanel
                          p={2}
                          bg={accordionBg}
                          borderColor={borderColor}
                          borderRadius="10px"
                        >
                          <VStack align="stretch" spacing={3}>
                            {/* Optimized HTML rendering */}
                            <Box
                              fontSize="sm"
                              color={subTextColor}
                              dangerouslySetInnerHTML={{
                                __html: (notification.message || 'No message')
                                  .replace(/\s+/g, ' ')
                                  .replace(/>\s+</g, '><')
                                  .trim()
                              }}
                              sx={{
                                '& p': {
                                  margin: '4px 0',
                                  lineHeight: '1.5'
                                },
                                '& p:first-of-type': {
                                  marginTop: 0
                                },
                                '& p:last-of-type': {
                                  marginBottom: 0
                                },
                                '& div': {
                                  margin: '4px 0'
                                }
                              }}
                            />

                            {/* Rest of your component remains the same */}
                            <HStack spacing={2}>
                              <Icon
                                as={MdOutlineAccessTime}
                                w={3}
                                h={3}
                                color="gray.500"
                              />
                              <Text fontSize="xs" color={subTextColor}>
                                Received:{' '}
                                {moment(notification.createdAt).format(
                                  'DD MMM YYYY, hh:mm A',
                                )}
                              </Text>
                            </HStack>

                            {notification.isRead && notification.readAt && (
                              <HStack spacing={2}>
                                <Icon
                                  as={MdCheckCircle}
                                  w={3}
                                  h={3}
                                  color="green.500"
                                />
                                <Text fontSize="xs" color="green.600">
                                  Read:{' '}
                                  {moment(notification.readAt).format(
                                    'DD MMM YYYY, hh:mm A',
                                  )}
                                </Text>
                              </HStack>
                            )}

                            <Flex justify="flex-end">
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedNotification(null);
                                  if (!notification.isRead) {
                                    markAsRead(notification._id);
                                  }
                                }}
                              >
                                Close
                              </Button>
                            </Flex>
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          {hasMore && !loading && notifications.length > 0 && (
            <Flex p={3} justify="center">
              <Button
                size="sm"
                variant="ghost"
                onClick={loadMore}
                isLoading={loadingMore}
                loadingText="Loading..."
              >
                Load More
              </Button>
            </Flex>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={ViewAll}
            loadingText="Redirecting..."
            w="100%"
          >
            View all
          </Button>
        </Box>

        {notifications.length > 0 && (
          <Flex
            p={3}
            borderTop="1px"
            borderColor={borderColor}
            justify="space-between"
            align="center"
            bg="gray.50"
          >
            <Text fontSize="xs" color={subTextColor}>
              Showing {notifications.length} notification
              {notifications.length !== 1 ? 's' : ''}
            </Text>
            <Badge colorScheme="blue" variant="subtle">
              {unreadCount} unread
            </Badge>
          </Flex>
        )}
      </MenuList>
    </Menu>
  );
};

export default NotificationPanel;
